/**
 * Utility functions for managing input sequences in forms
 */

/**
 * The class a floating bottom button bar (`cs-page-action-buttons`) puts on itself while the
 * page it belongs to is the one on screen. See `shouldSkipSequenceCandidate` for why the
 * forward scan below has to care.
 */
const VISIBLE_ACTION_BAR_CLASS = 'visible';

/** What `shouldSkipSequenceCandidate` needs to know about one candidate. */
export interface SequenceCandidateDescription {
  /** The `input-sequence` value of the element the user is currently on. '' when it names no group. */
  currentGroup: string;
  /** The `input-sequence` value of the candidate being considered. '' when it names no group. */
  candidateGroup: string;
  /** True when the candidate sits inside a button bar belonging to a page that is not on screen. */
  candidateIsInHiddenActionBar: boolean;
}

/**
 * Should the forward scan step over this candidate?
 *
 * Two independent reasons to skip, either one enough on its own (#4967):
 *
 * 1. **It belongs to the same group as the field the user is on.** `input-sequence` may
 *    carry a group name. Fields sharing a group show the SAME underlying value in different
 *    units — the portion screen's Amount block shows grams and, for a food with its own
 *    measures, "2 slices" beside it, both bound to one number. Moving from one to the other
 *    is not a step forward, it is a second confirmation with a friendlier label. An EMPTY
 *    group name is not a group: every page that used this mechanism before grouping existed
 *    carries a bare `input-sequence`, and treating two blanks as a match would stop the
 *    keypad finding the next field on all of them.
 *
 * 2. **It sits in a button bar that is not on screen.** On a phone `cs-page-action-buttons`
 *    moves itself out of its own page and into one container the whole app shares, and that
 *    container holds one bar for every page still alive underneath in the navigation stack.
 *    An inactive page's bar is hidden by sliding it off-screen — a CSS transform, which the
 *    plain `display`/`visibility` check in `findNextVisibleSequenceElement` does not catch —
 *    and it comes FIRST in the container. Without this the keypad could borrow an older,
 *    invisible page's action button and act on the wrong page.
 *
 * Kept free of any DOM API so it can be unit tested in plain Node, where the client tests
 * run. `components/cs-page-action-buttons/action-buttons-owner.ts` splits itself the same way
 * for the same reason.
 */
export function shouldSkipSequenceCandidate(description: SequenceCandidateDescription): boolean {
  if (description.candidateIsInHiddenActionBar) {
    return true;
  }
  // a blank group name means "no group", so two blanks are not a match
  if (description.currentGroup && description.currentGroup === description.candidateGroup) {
    return true;
  }
  return false;
}

/** Reads an element's `input-sequence` group name, or '' when it names none. */
function readSequenceGroup(element: HTMLElement): string {
  return element.getAttribute('input-sequence')?.trim() || '';
}

/**
 * Is this element inside a floating button bar belonging to a page that is not on screen?
 * Answers false when the element is not in a button bar at all.
 */
function isInHiddenActionBar(element: HTMLElement): boolean {
  const actionBar = element.closest('cs-page-action-buttons');
  if (!actionBar) return false;
  return !actionBar.classList.contains(VISIBLE_ACTION_BAR_CLASS);
}

/**
 * Find the next visible element in an input sequence
 * @param currentElement The current element in the sequence
 * @returns The next visible element with input-sequence attribute, or undefined if none found
 */
export function findNextVisibleSequenceElement(currentElement: HTMLElement): HTMLElement | undefined {
  // Look for elements with input-sequence attribute in DOM order
  const sequenceElements = Array.from(document.querySelectorAll('[input-sequence]')) as HTMLElement[];
  const currentIndex = sequenceElements.indexOf(currentElement);
  
  if (currentIndex === -1 || currentIndex === sequenceElements.length - 1) {
    // Not in sequence or last in sequence
    return undefined;
  }

  const currentGroup = readSequenceGroup(currentElement);

  // Find the next visible element in sequence
  for (let i = currentIndex + 1; i < sequenceElements.length; i++) {
    const nextElement = sequenceElements[i];
    
    // Skip hidden elements
    const style = window.getComputedStyle(nextElement);
    if (style.display === 'none' || style.visibility === 'hidden') {
      continue;
    }
    
    // Skip elements that are not visible due to parent
    let parent = nextElement.parentElement;
    let isVisible = true;
    while (parent && parent !== document.body) {
      const parentStyle = window.getComputedStyle(parent);
      if (parentStyle.display === 'none' || parentStyle.visibility === 'hidden') {
        isVisible = false;
        break;
      }
      parent = parent.parentElement;
    }
    
    if (!isVisible) {
      continue;
    }

    // Skip a candidate that shows the same value we are already editing, or that belongs to
    // a page which is not on screen. The rule itself is in shouldSkipSequenceCandidate.
    if (shouldSkipSequenceCandidate({
      currentGroup,
      candidateGroup: readSequenceGroup(nextElement),
      candidateIsInHiddenActionBar: isInHiddenActionBar(nextElement),
    })) {
      continue;
    }
    
    // Found next visible element
    return nextElement;
  }
  
  return undefined;
}
